import Vue from 'vue';
import Component from "vue-class-component";
import 'filepond/dist/filepond.min.css';
import {Prop, Watch} from "vue-property-decorator";
import {ResultError, TempFileModel, TempFileUploadDestinationModel, UserTemporaryFilesClient} from "@/api-client";
import {ls} from "@/CultureService";
import Loader from "@/features/shared/components/Loader";

@Component
export default class FileUploaderBase extends Vue {
    $refs!: { uploader: any }

    @Prop({ type: File, required: false })
    preloadedFile!: File | null;

    @Prop({default: () => ([])})
    value!: TempFileModel[];

    @Prop({default: () => new Loader()})
    loader!: Loader;
    

    @Watch("preloadedFile", { immediate: true })
    onPreloadedFileChange(newFile: File | null)
    {
        if (newFile)
        {
            this.addFileToFilePond(newFile);
        }
    }

    files: TempFileModel[] = this.value || [];
    
    warning = "";
    
    server = {
        process: (fieldName: any, file: any, metadata: any, load: any, error: any, progress: any, abort: any, transfer: any, options: any) => {
            const request = new XMLHttpRequest();
            new UserTemporaryFilesClient()
                .createUploadUrl(file.name)
                .then((uploadDestination : TempFileUploadDestinationModel) => {
                    if (!uploadDestination) {
                        error(this.$ls.Components_Files_Text_Error);
                        return;
                    }
                    
                    request.open('PUT', uploadDestination.uploadUrl);
                    request.setRequestHeader('content-type', 'application/octet-stream');
                    // Should call the progress method to update the progress to 100% before calling load
                    // Setting computable to false switches the loading indicator to infinite mode
                    request.upload.onprogress = (e) => {
                        progress(e.lengthComputable, e.loaded, e.total);
                    };
                    
                    // Should call the load method when done and pass the returned server file id
                    // this server file id is then used later on when reverting or restoring a file
                    // so your server knows which file to return without exposing that info to the client
                    request.onload = function () {
                        if (request.status >= 200 && request.status < 300) {
                            // the load method accepts either a string (id) or an object
                            load(uploadDestination.fileId);
                            
                            const tempFileLocation = new TempFileModel();
                            tempFileLocation.id = uploadDestination.fileId;
                            tempFileLocation.fileName = file.name;
                            file.tempFile = tempFileLocation;
                        } else {
                            // Can call the error method if something is wrong, should exit after
                            error(ls.Components_Files_Text_OnloadError);
                        }
                    };
                    
                    request.send(file);
                }).catch((e: ResultError) => {
                    error(e.messagesFlattened.join(", "))
                });
            
            return {
                // This one is called when cancelling file uploads that take a long time (more than a few seconds) to finish.
                abort: () => {
                    // This function is entered if the user has tapped the cancel button
                    request.abort();
                    // Let FilePond know the request has been cancelled
                    abort();
                }
            };
        },
        
        // This one is called when cancelling file uploads that finish quickly.
        revert: null // Don't make a DELETE call to the server.
    };

    created() {
        this.$watch(() => this.files, files => {
            this.warning = "";
            this.$emit("input", files);
        })
    }

    addFileToFilePond(file: File)
    {
        (this.$refs.uploader as any).addFile(file);
    }
    
    onAddfile() {
        this.$refs.uploader.processFiles();
    }

    onProcessFileStart() {
        this.loader.startLoading();
    }
    
    onFileProcessed(error: any, file: any): void {
        if (!error) {
            this.files.push(file.file.tempFile);
        }
        this.loader.endLoading();
    }

    onProcessFileRevert(singleFileProcessor: any) {
        singleFileProcessor.abortProcessing();
        this.loader.endLoading();
    }

    onRemoveFile(error: any, file: any): void {
        if (!error) {
            this.$delete(this.files, this.files.indexOf(file.file.tempFile));
        }
    }

    onWarning(warning: { type: string, code: number, body: string, headers: any }) {
        if (warning.body == "Max files") {
            this.warning = ls.Components_Files_Warning_MaxFileCountIs + " " + this.$refs.uploader.maxFiles;
        } else {
            throw warning;
        }
    }
}